'use client';

import React, { useEffect, useRef } from 'react';
import { cn, TCustomModalProps } from 'types';
import { OverlayContainer } from '../overlay-container';
import { Icon, IconType } from '../icons';

export const CustomModal = ({
  open,
  children,
  autoCloseOnFocusOut = false,
  headerTitle,
  className,
  overlayColor = 'var(--color-ultramarine80)',
  onCloseClick,
  allowScrolling = false,
  withoutOverlay = false,
}: TCustomModalProps) => {
  let modalRef = useRef(null);

  const handleClickOutside = (e: any) => {
    if (modalRef.current && autoCloseOnFocusOut && !modalRef.current.contains(e.target)) {
      onCloseClick && onCloseClick();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (open && !allowScrolling && !withoutOverlay) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const handleEsc = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onCloseClick();
        }
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [open]);

  const CloseButton = ({ className = '' }) => (
    <div className="flex justify-center items-center">
      <button
        className={cn(
          'closing-button',
          'size-8 cursor-pointer rounded-full bg-lightGrey flex items-center justify-center shrink-0',
          'hover:bg-turquoise active:bg-markTurquoise',
          className
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          onCloseClick();
        }}
      >
        <Icon icon={IconType.Close} width={20} height={20} />
      </button>
    </div>
  );

  const DynamicModalWrapper = withoutOverlay ? React.Fragment : OverlayContainer;

  return (
    open && (
      <DynamicModalWrapper {...(DynamicModalWrapper ? { overlayBgColor: overlayColor } : {})}>
        <div
          ref={modalRef}
          className={cn(
            'rounded-lg bg-white shadow-[0_8px_24px_2px_rgba(48,_48,_111,_0.04),_0_2px_16px_0_rgba(48,_48,_111,_0.08)]',
            'max-w-3xl max-h-[calc(100vh_-_6rem)]',
            !headerTitle && 'relative',
            className
          )}
        >
          {headerTitle ? (
            <div className="flex items-center justify-between gap-8 px-6 py-4 border-b-1 border-neutralGrey">
              {typeof headerTitle === 'string' ? (
                <h3 className="text-xl/[180%] font-extrabold text-gray-900 flex-1">{headerTitle}</h3>
              ) : (
                headerTitle
              )}
              <CloseButton />
            </div>
          ) : (
            <CloseButton className="absolute top-4 right-6" />
          )}
          {children}
        </div>
      </DynamicModalWrapper>
    )
  );
};
